import {
  ClosestEdgeRunType,
  EdgeRunFrameType,
  EdgeRunType,
  OpeningEdgeType,
  OpeningOffsetItemType,
  OpeningOffsetRangeType,
  OpeningOnWallType,
  OpeningPlacementType,
  OpeningPointType,
  OpeningRunBoundsType,
  OpeningSpanType,
  OrientedCardinalWallEdgeType,
  ResolvedOpeningOffsetType
} from 'utils/opening-position-types';
import { numericOpeningPosition } from 'utils/opening-position-value';
import {
  outwardNormalForCardinal,
  shouldReverseSegmentEndpoints
} from 'utils/wall-segment';

export const OPENING_SNAP_FT = 0.5;

export function openingRunBounds(lengthFt: number): OpeningRunBoundsType {
  return { lengthFt, overhangStartFt: 0, overhangEndFt: 0 };
}

export const EMPTY_OPENING_RUN_BOUNDS: OpeningRunBoundsType =
  openingRunBounds(0);

export function isHorizontalOpeningEdge(edge: OpeningEdgeType): boolean {
  return edge === 'top' || edge === 'bottom';
}

export function orientCardinalWallEdge(
  p1: OpeningPointType,
  p2: OpeningPointType,
  edge: OpeningEdgeType
): OrientedCardinalWallEdgeType {
  const reverse = shouldReverseSegmentEndpoints(p1, p2, edge);
  const start = reverse ? p2 : p1;
  const end = reverse ? p1 : p2;
  return {
    start,
    end,
    lengthFt: Math.hypot(end.x - start.x, end.y - start.y)
  };
}

export function runDirection(run: EdgeRunType): OpeningPointType {
  const dx = run.end.x - run.start.x;
  const dy = run.end.y - run.start.y;
  const length = Math.hypot(dx, dy);
  if (length === 0) {
    return isHorizontalOpeningEdge(run.edge) ? { x: 1, y: 0 } : { x: 0, y: 1 };
  }
  return { x: dx / length, y: dy / length };
}

function perpendicularTowardCardinal(
  direction: OpeningPointType,
  edge: OpeningEdgeType
): OpeningPointType {
  const candidate = { x: direction.y + 0, y: -direction.x + 0 };
  const cardinalNormal = outwardNormalForCardinal(edge);
  const agreement =
    candidate.x * cardinalNormal.x + candidate.y * cardinalNormal.y;
  return agreement >= 0
    ? candidate
    : { x: -candidate.x + 0, y: -candidate.y + 0 };
}

export function runOutwardNormal(run: EdgeRunType): OpeningPointType {
  return (
    run.outward ?? perpendicularTowardCardinal(runDirection(run), run.edge)
  );
}

export function runFrame(run: EdgeRunType): EdgeRunFrameType {
  const direction = runDirection(run);
  const outward = runOutwardNormal(run);
  const inward = { x: -outward.x + 0, y: -outward.y + 0 };
  const perpendicular = { x: -direction.y, y: direction.x };
  const inwardSign =
    inward.x * perpendicular.x + inward.y * perpendicular.y >= 0 ? 1 : -1;
  return {
    direction,
    outward,
    inward,
    directionDeg: (Math.atan2(direction.y, direction.x) * 180) / Math.PI,
    inwardSign
  };
}

export function pointAlongRun(
  run: EdgeRunType,
  offsetFt: number
): OpeningPointType {
  const direction = runDirection(run);
  return {
    x: run.start.x + direction.x * offsetFt,
    y: run.start.y + direction.y * offsetFt
  };
}

export function offsetOnRun(run: EdgeRunType, point: OpeningPointType): number {
  const direction = runDirection(run);
  return (
    (point.x - run.start.x) * direction.x +
    (point.y - run.start.y) * direction.y
  );
}

export function openingRunExtentFt(bounds: OpeningRunBoundsType): number {
  return bounds.lengthFt + bounds.overhangStartFt + bounds.overhangEndFt;
}

export function openingOffsetRangeOnRun(
  widthFt: number,
  bounds: OpeningRunBoundsType
): OpeningOffsetRangeType {
  const halfWidthFt = widthFt / 2;
  const startReachFt = Math.min(bounds.overhangStartFt, halfWidthFt);
  const endReachFt = Math.min(bounds.overhangEndFt, halfWidthFt);
  const minFt = startReachFt > 0 ? -startReachFt : 0;
  const maxFt = Math.max(minFt, bounds.lengthFt - widthFt + endReachFt);
  return { minFt, maxFt };
}

export function clampOpeningOffsetOnRun(
  offsetFt: number,
  widthFt: number,
  bounds: OpeningRunBoundsType
): number {
  const { minFt, maxFt } = openingOffsetRangeOnRun(widthFt, bounds);
  return Math.min(Math.max(minFt, offsetFt), maxFt);
}

export function clampOpeningOffset(
  offsetFt: number,
  widthFt: number,
  wallLengthFt: number
): number {
  return clampOpeningOffsetOnRun(
    offsetFt,
    widthFt,
    openingRunBounds(wallLengthFt)
  );
}

// positionOnWall is feet from the start of the space's exterior cardinal run for that edge
export function normalizeOpeningOnWall<T extends OpeningOnWallType>(
  item: T,
  runBounds: OpeningRunBoundsType
): T {
  const positionOnWall = numericOpeningPosition(item.positionOnWall);
  if (positionOnWall === undefined) {
    if (item.positionOnWall === undefined) {
      return item;
    }

    const next = { ...item };
    delete next.positionOnWall;
    return next;
  }

  return {
    ...item,
    positionOnWall: clampOpeningOffsetOnRun(
      positionOnWall,
      item.width,
      runBounds
    )
  };
}

export function snapOpeningOffset(offsetFt: number): number {
  return Math.round(offsetFt / OPENING_SNAP_FT) * OPENING_SNAP_FT;
}

export function closestEdgeRunToPoint(
  runs: EdgeRunType[],
  point: { x: number; y: number }
): ClosestEdgeRunType | null {
  let best: ClosestEdgeRunType | null = null;

  runs.forEach((run) => {
    const offsetFt = Math.max(
      0,
      Math.min(run.lengthFt, offsetOnRun(run, point))
    );
    const closest = pointAlongRun(run, offsetFt);
    const distanceFt = Math.hypot(point.x - closest.x, point.y - closest.y);

    if (!best || distanceFt < best.distanceFt) {
      best = {
        run,
        edge: run.edge,
        offsetFt,
        lengthFt: run.lengthFt,
        distanceFt
      };
    }
  });

  return best;
}

export function resolveOpeningOffsets(
  items: OpeningOffsetItemType[],
  wallLengthFt: number
): ResolvedOpeningOffsetType[] {
  const unpositionedCount = items.filter(
    (item) => numericOpeningPosition(item.positionOnWall) === undefined
  ).length;
  let unpositionedIndex = 0;

  return items.map((item) => {
    const stored = numericOpeningPosition(item.positionOnWall);
    if (stored !== undefined) {
      return {
        id: item.id,
        offsetFt: stored
      };
    }

    unpositionedIndex += 1;
    const raw =
      (wallLengthFt / (unpositionedCount + 1)) * unpositionedIndex -
      item.widthFt / 2;
    return {
      id: item.id,
      offsetFt: clampOpeningOffset(raw, item.widthFt, wallLengthFt)
    };
  });
}

export function openingCenterTs(
  items: OpeningOffsetItemType[],
  wallLengthFt: number
): number[] {
  if (wallLengthFt <= 0) {
    return items.map(() => 0);
  }

  return resolveOpeningOffsets(items, wallLengthFt).map((resolved, index) => {
    const widthFt = Math.min(items[index].widthFt, wallLengthFt);
    return (resolved.offsetFt + widthFt / 2) / wallLengthFt;
  });
}

export function openingSpanOnRun(
  run: EdgeRunType,
  offsetFt: number,
  widthFt: number
): OpeningSpanType {
  const width = Math.min(widthFt, openingRunExtentFt(run));
  const offset = clampOpeningOffsetOnRun(offsetFt, width, run);

  return {
    p1: pointAlongRun(run, offset),
    p2: pointAlongRun(run, offset + width)
  };
}

export function projectOpeningPlacement(
  point: { x: number; y: number },
  runs: EdgeRunType[],
  widthFt: number
): OpeningPlacementType | null {
  const hit = closestEdgeRunToPoint(runs, point);
  if (!hit) {
    return null;
  }

  return {
    edge: hit.edge,
    offsetFt: snapOpeningOffset(
      clampOpeningOffsetOnRun(hit.offsetFt - widthFt / 2, widthFt, hit.run)
    ),
    run: hit.run
  };
}
